import React, { useState, useEffect, useRef } from 'react';
import { 
    Users, Plus, Edit, Trash2, Mail, Phone, Shield, UserPlus, 
    Upload, FileText, Eye, Download, X, CheckCircle, AlertCircle,
    CreditCard, Home, Calendar, DollarSign, FolderOpen
} from 'lucide-react';
import api from '../services/api';
import Modal from '../components/Common/Modal';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';


const TenantManagement = () => {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showKycModal, setShowKycModal] = useState(false);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [kycData, setKycData] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [activeTab, setActiveTab] = useState('kyc');
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
        role: 'tenant',
        id_number: '',
        kra_pin: '',
        emergency_contact: '',
        emergency_phone: '',
        address: '',
        city: '',
        postal_code: ''
    });

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editFormData, setEditFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: 'tenant',
        status: 'active'
    });
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState(null);

    // Password change state
    const [showPasswordSection, setShowPasswordSection] = useState(false);
    const [passwordData, setPasswordData] = useState({
        new_password: '',
        confirm_password: ''
    });
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordError, setPasswordError] = useState(null);
    const [passwordSuccess, setPasswordSuccess] = useState(null);

    // Pagination state
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const PAGE_SIZE = 20;

    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    const searchTimeoutRef = useRef(null);

    // Delete confirmation state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingUserId, setDeletingUserId] = useState(null);
    const [deletingUserEmail, setDeletingUserEmail] = useState('');
    const [deletingLoading, setDeletingLoading] = useState(false);

    useEffect(() => {
        fetchUsers(1);
    }, []);

    // Re-fetch when page changes
    useEffect(() => {
        if (!loading) fetchUsers(page);
    }, [page]);

    // Search with debounce
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setPage(1);
            fetchUsers(1, value);
        }, 400);
    };



    const fetchUsers = async (pageNum, search) => {
        try {
            const currentPage = pageNum || page;
            const currentSearch = search || searchTerm || '';
            const params = `page=${currentPage}&limit=${PAGE_SIZE}${currentSearch ? `&search=${encodeURIComponent(currentSearch)}` : ''}`;
            const response = await api.get(`/tenants/users?${params}`);
            let usersData = [];
            if (response.data.success && response.data.data) {
                usersData = response.data.data.data || response.data.data || [];
                const pagination = response.data.data.pagination;
                if (pagination) {
                    setTotalPages(pagination.pages || 1);
                    setTotalUsers(pagination.total || 0);
                }
            }
            setTenants(usersData);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };


    const fetchUserKyc = async (userId) => {
        try {
            const response = await api.get(`/tenants/users/${userId}/kyc`);
            setKycData(response.data.data);
        } catch (error) {
            console.error('Error fetching KYC:', error);
            setKycData(null);
        }
    };

    const fetchUserDocuments = async (userId) => {
        try {
            const response = await api.get(`/tenants/users/${userId}/documents`);
            setDocuments(response.data.data || []);
        } catch (error) {
            console.error('Error fetching documents:', error);
            setDocuments([]);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            await api.post('/tenants/users', formData);
            setShowModal(false);
            setFormData({
                email: '',
                password: '',
                first_name: '',
                last_name: '',
                phone: '',
                role: 'tenant',
                id_number: '',
                kra_pin: '',
                emergency_contact: '',
                emergency_phone: '',
                address: '',
                city: '',
                postal_code: ''
            });
            fetchUsers();
            alert('User created successfully!');
        } catch (error) {
            console.error('Error creating user:', error);
            alert(error.response?.data?.error || 'Failed to create user');
        }
    };

    const handleUpdateKyc = async () => {
        try {
            await api.put(`/tenants/users/${selectedUser.id}/kyc`, kycData);
            alert('KYC information updated successfully!');
            fetchUserKyc(selectedUser.id);
        } catch (error) {
            console.error('Error updating KYC:', error);
            alert('Failed to update KYC information');
        }
    };

    const handleVerifyKyc = async () => {
        try {
            await api.post(`/tenants/users/${selectedUser.id}/kyc/verify`);
            alert('KYC verified successfully!');
            fetchUserKyc(selectedUser.id);
        } catch (error) {
            console.error('Error verifying KYC:', error);
            alert('Failed to verify KYC');
        }
    };

    const handleUploadDocument = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        setUploading(true);
        const uploadData = new FormData();
        uploadData.append('document', file);
        uploadData.append('document_type', 'id_card');
        
        try {
            await api.post(`/tenants/users/${selectedUser.id}/documents`, uploadData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            fetchUserDocuments(selectedUser.id);
            alert('Document uploaded successfully!');
        } catch (error) {
            console.error('Error uploading document:', error);
            alert('Failed to upload document');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteDocument = async (docId) => {
        if (window.confirm('Are you sure you want to delete this document?')) {
            try {
                await api.delete(`/tenants/users/${selectedUser.id}/documents/${docId}`);
                fetchUserDocuments(selectedUser.id);
                alert('Document deleted successfully!');
            } catch (error) {
                console.error('Error deleting document:', error);
                alert('Failed to delete document');
            }
        }
    };

    const viewKyc = async (user) => {
        setSelectedUser(user);
        await fetchUserKyc(user.id);
        await fetchUserDocuments(user.id);
        setActiveTab('kyc');
        setShowKycModal(true);
    };

    // ==================== EDIT USER ====================
    const openEditModal = (user) => {
        setEditingUser(user);
        setEditFormData({
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            email: user.email || '',
            phone: user.phone || '',
            role: user.role || 'tenant',
            status: user.status || 'active'
        });
        setEditError(null);
        setShowEditModal(true);
    };

    const handleEditUser = async (e) => {
        e.preventDefault();
        setEditLoading(true);
        setEditError(null);
        try {
            await api.put(`/tenants/users/${editingUser.id}`, editFormData);
            setShowEditModal(false);
            setEditingUser(null);
            fetchUsers();
            alert('User updated successfully!');
        } catch (error) {
            setEditError(error.response?.data?.error || 'Failed to update user');
        } finally {
            setEditLoading(false);
        }
    };

    // ==================== DELETE USER ====================
    const confirmDeleteUser = (user) => {
        setDeletingUserId(user.id);
        setDeletingUserEmail(user.email);
        setShowDeleteConfirm(true);
    };

    const handleDeleteUser = async () => {
        setDeletingLoading(true);
        try {
            await api.delete(`/tenants/users/${deletingUserId}`);
            setShowDeleteConfirm(false);
            setDeletingUserId(null);
            setDeletingUserEmail('');
            fetchUsers();
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to delete user');
        } finally {
            setDeletingLoading(false);
        }
    };

    const getRoleBadge = (role) => {
        const colors = {
            landlord: 'bg-purple-100 text-purple-800',
            caretaker: 'bg-blue-100 text-blue-800',
            tenant: 'bg-green-100 text-green-800',
            superadmin: 'bg-red-100 text-red-800'
        };
        return colors[role] || colors.tenant;
    };

    const getStatusBadge = (status) => {
        const colors = {
            active: 'bg-green-100 text-green-800',
            suspended: 'bg-red-100 text-red-800',
            pending: 'bg-yellow-100 text-yellow-800'
        };
        return colors[status] || colors.pending;
    };

    if (loading) return <LoadingSpinner size="lg" />;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Tenant Management</h1>
                    <p className="text-gray-600">Manage tenants, caretakers, staff accounts, and KYC documents</p>
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                    <UserPlus className="h-5 w-5 mr-2" />
                    Add User
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Total Tenants</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {tenants.filter(t => t.role === 'tenant').length}
                            </p>
                        </div>
                        <Users className="h-8 w-8 text-indigo-600" />
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Active Leases</p>
                            <p className="text-2xl font-bold text-green-600">0</p>
                        </div>
                        <Home className="h-8 w-8 text-green-600" />
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Monthly Revenue</p>
                            <p className="text-2xl font-bold text-blue-600">KES 0</p>
                        </div>
                        <DollarSign className="h-8 w-8 text-blue-600" />
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Pending KYC</p>
                            <p className="text-2xl font-bold text-yellow-600">
                                {tenants.filter(t => !t.kyc_verified && t.role === 'tenant').length}
                            </p>
                        </div>
                        <Shield className="h-8 w-8 text-yellow-600" />
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                    type="text"
                    placeholder="Search by name, email, phone or ID number..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name / Contact</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">ID Number</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Status</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">KYC</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Joined</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase sticky right-0 bg-gray-50 shadow-lg">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {tenants.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-4">
                                        <div className="flex items-center">
                                            <div className="h-9 w-9 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                                <Users className="h-4 w-4 text-indigo-600" />
                                            </div>
                                            <div className="ml-2 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate max-w-[160px]">
                                                    {user.first_name} {user.last_name}
                                                </p>
                                                <div className="flex items-center text-xs text-gray-500 space-x-2">
                                                    <span className="truncate max-w-[120px]">{user.email}</span>
                                                    {user.phone && <span className="hidden sm:inline">· {user.phone}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-600 hidden md:table-cell">
                                        <span className="truncate max-w-[100px] inline-block">{user.id_number || '-'}</span>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${getRoleBadge(user.role)}`}>
                                            {user.role}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap hidden sm:table-cell">
                                        <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadge(user.status)}`}>
                                            {user.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4 text-center hidden lg:table-cell">
                                        <button
                                            onClick={() => viewKyc(user)}
                                            className="text-indigo-600 hover:text-indigo-800 p-1 rounded hover:bg-indigo-50"
                                            title="View KYC"
                                        >
                                            <Shield className="h-4 w-4" />
                                        </button>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap hidden lg:table-cell">
                                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                                    </td>
                                    <td className="px-4 py-4 sticky right-0 bg-white shadow-lg">
                                        <div className="flex items-center justify-center space-x-1">
                                            <button
                                                onClick={() => openEditModal(user)}
                                                className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                                title="Edit User"
                                            >
                                                <Edit className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => confirmDeleteUser(user)}
                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Delete User"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                
                <Pagination
                    page={page}
                    totalPages={totalPages}
                    total={totalUsers}
                    limit={PAGE_SIZE}
                    onPageChange={(newPage) => {
                        setPage(newPage);
                        fetchUsers(newPage);
                    }}
                />
            </div>

            {/* Create User Modal */}

            <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add New User" size="lg">
                <form onSubmit={handleCreateUser} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                            <input
                                type="text"
                                value={formData.first_name}
                                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                            <input
                                type="text"
                                value={formData.last_name}
                                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                required
                            />
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                            <input
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                placeholder="254700000000"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">ID Number</label>
                            <input
                                type="text"
                                value={formData.id_number}
                                onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                placeholder="National ID / Passport"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">KRA PIN</label>
                            <input
                                type="text"
                                value={formData.kra_pin}
                                onChange={(e) => setFormData({ ...formData, kra_pin: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                placeholder="KRA PIN"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                        <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                            <select
                                value={formData.role}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                            >
                                <option value="tenant">Tenant</option>
                                <option value="caretaker">Caretaker</option>
                                <option value="landlord">Landlord</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <select
                                value={formData.status || 'active'}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                            >
                                <option value="active">Active</option>
                                <option value="suspended">Suspended</option>
                                <option value="pending">Pending</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
                        <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                            Create User
                        </button>
                    </div>
                </form>
            </Modal>

            {/* ==================== EDIT USER MODAL ==================== */}
            <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title={`Edit User: ${editingUser?.first_name} ${editingUser?.last_name}`} size="md">
                <form onSubmit={handleEditUser} className="space-y-4">
                    {editError && (
                        <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                            <div className="flex items-start">
                                <XCircle className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                                <p className="text-sm text-red-600">{editError}</p>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                            <input
                                type="text"
                                value={editFormData.first_name}
                                onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                            <input
                                type="text"
                                value={editFormData.last_name}
                                onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                            <input
                                type="email"
                                value={editFormData.email}
                                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                            <input
                                type="tel"
                                value={editFormData.phone}
                                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                placeholder="254700000000"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                            <select
                                value={editFormData.role}
                                onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                            >
                                <option value="tenant">Tenant</option>
                                <option value="caretaker">Caretaker</option>
                                <option value="landlord">Landlord</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <select
                                value={editFormData.status}
                                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                            >
                                <option value="active">Active</option>
                                <option value="suspended">Suspended</option>
                                <option value="pending">Pending</option>
                            </select>
                        </div>
                    </div>

                    {/* Password Section */}
                    <div className="pt-2">
                        <div className="border-t border-gray-200 my-4"></div>
                        <button
                            type="button"
                            onClick={() => {
                                setShowPasswordSection(!showPasswordSection);
                                setPasswordError(null);
                                setPasswordSuccess(null);
                                setPasswordData({ new_password: '', confirm_password: '' });
                            }}
                            className="flex items-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                            </svg>
                            {showPasswordSection ? 'Hide Password Change' : 'Change Password'}
                        </button>

                        {showPasswordSection && (
                            <div className="mt-3 space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <h4 className="text-sm font-medium text-gray-700">Set New Password</h4>
                                
                                {passwordError && (
                                    <div className="p-2 rounded bg-red-50 border border-red-200">
                                        <p className="text-sm text-red-600">{passwordError}</p>
                                    </div>
                                )}
                                {passwordSuccess && (
                                    <div className="p-2 rounded bg-green-50 border border-green-200">
                                        <p className="text-sm text-green-600">{passwordSuccess}</p>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">New Password *</label>
                                    <input
                                        type="password"
                                        value={passwordData.new_password}
                                        onChange={(e) => {
                                            setPasswordData({ ...passwordData, new_password: e.target.value });
                                            setPasswordError(null);
                                            setPasswordSuccess(null);
                                        }}
                                        className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                        placeholder="At least 6 characters"
                                        minLength={6}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                                    <input
                                        type="password"
                                        value={passwordData.confirm_password}
                                        onChange={(e) => {
                                            setPasswordData({ ...passwordData, confirm_password: e.target.value });
                                            setPasswordError(null);
                                            setPasswordSuccess(null);
                                        }}
                                        className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                        placeholder="Re-enter new password"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={async () => {
                                        setPasswordError(null);
                                        setPasswordSuccess(null);
                                        
                                        if (!passwordData.new_password || passwordData.new_password.length < 6) {
                                            setPasswordError('Password must be at least 6 characters');
                                            return;
                                        }
                                        if (passwordData.new_password !== passwordData.confirm_password) {
                                            setPasswordError('Passwords do not match');
                                            return;
                                        }
                                        
                                        setPasswordLoading(true);
                                        try {
                                            await api.put(`/tenants/users/${editingUser.id}/password`, {
                                                new_password: passwordData.new_password
                                            });
                                            setPasswordSuccess('Password changed successfully!');
                                            setPasswordData({ new_password: '', confirm_password: '' });
                                        } catch (error) {
                                            setPasswordError(error.response?.data?.error || 'Failed to change password');
                                        } finally {
                                            setPasswordLoading(false);
                                        }
                                    }}
                                    disabled={passwordLoading}
                                    className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                >
                                    {passwordLoading ? 'Changing...' : 'Change Password'}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={() => setShowEditModal(false)}
                            className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={editLoading}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {editLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* ==================== DELETE USER CONFIRMATION ==================== */}
            <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title="Confirm Delete User" size="sm">
                <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start">
                            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-medium text-red-800">Are you sure?</p>
                                <p className="text-sm text-red-600 mt-1">
                                    You are about to delete user <strong>{deletingUserEmail}</strong>. This action cannot be undone.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => setShowDeleteConfirm(false)}
                            className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleDeleteUser}
                            disabled={deletingLoading}
                            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {deletingLoading ? 'Deleting...' : 'Delete User'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* KYC/Documents Modal */}
            <Modal isOpen={showKycModal} onClose={() => setShowKycModal(false)} title={`Manage: ${selectedUser?.first_name} ${selectedUser?.last_name}`} size="lg">
                {/* Tabs */}
                <div className="flex border-b mb-4">
                    <button
                        onClick={() => setActiveTab('kyc')}
                        className={`px-4 py-2 font-medium ${activeTab === 'kyc' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500'}`}
                    >
                        KYC Information
                    </button>
                    <button
                        onClick={() => setActiveTab('documents')}
                        className={`px-4 py-2 font-medium ${activeTab === 'documents' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500'}`}
                    >
                        Documents ({documents.length})
                    </button>
                </div>

                {activeTab === 'kyc' && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">ID Number</label>
                                <input
                                    type="text"
                                    value={kycData?.id_number || ''}
                                    onChange={(e) => setKycData({ ...kycData, id_number: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 p-2"
                                    placeholder="National ID / Passport"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">KRA PIN</label>
                                <input
                                    type="text"
                                    value={kycData?.kra_pin || ''}
                                    onChange={(e) => setKycData({ ...kycData, kra_pin: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 p-2"
                                    placeholder="KRA PIN"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact</label>
                                <input
                                    type="text"
                                    value={kycData?.emergency_contact || ''}
                                    onChange={(e) => setKycData({ ...kycData, emergency_contact: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 p-2"
                                    placeholder="Contact name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Phone</label>
                                <input
                                    type="tel"
                                    value={kycData?.emergency_phone || ''}
                                    onChange={(e) => setKycData({ ...kycData, emergency_phone: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 p-2"
                                    placeholder="Phone number"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                            <textarea
                                value={kycData?.address || ''}
                                onChange={(e) => setKycData({ ...kycData, address: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 p-2"
                                rows={2}
                                placeholder="Full address"
                            />
                        </div>
                        <div className="flex justify-between items-center pt-4">
                            <div>
                                {kycData?.kyc_verified ? (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                                        <CheckCircle className="h-4 w-4 mr-1" />
                                        KYC Verified
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
                                        <AlertCircle className="h-4 w-4 mr-1" />
                                        Pending Verification
                                    </span>
                                )}
                            </div>
                            <div className="flex space-x-2">
                                <button onClick={handleUpdateKyc} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                                    Save KYC
                                </button>
                                {!kycData?.kyc_verified && (
                                    <button onClick={handleVerifyKyc} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                                        Verify KYC
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'documents' && (
                    <div className="space-y-4">
                        {/* Upload Area */}
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-500" onClick={() => document.getElementById('file-upload').click()}>
                            <Upload className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-600">Click to upload ID document</p>
                            <p className="text-xs text-gray-500">PDF, JPG, PNG (Max 5MB)</p>
                            <input
                                id="file-upload"
                                type="file"
                                onChange={handleUploadDocument}
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png"
                                disabled={uploading}
                            />
                            {uploading && <div className="mt-2 text-indigo-600">Uploading...</div>}
                        </div>

                        {/* Documents List */}
                        <div className="space-y-2">
                            <h3 className="font-semibold text-gray-900">Uploaded Documents</h3>
                            {documents.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <FolderOpen className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                                    <p>No documents uploaded yet</p>
                                </div>
                            ) : (
                                documents.map((doc) => (
                                    <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <FileText className="h-5 w-5 text-gray-500" />
                                            <div>
                                                <p className="font-medium">{doc.document_name || doc.name}</p>
                                                <p className="text-xs text-gray-500">
                                                    {new Date(doc.created_at).toLocaleDateString()} • 
                                                    {doc.file_size ? ` ${(doc.file_size / 1024).toFixed(2)} KB` : ''}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex space-x-2">
                                            <a href={doc.document_path || doc.url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800">
                                                <Eye className="h-4 w-4" />
                                            </a>
                                            <a href={doc.document_path || doc.url} download className="text-green-600 hover:text-green-800">
                                                <Download className="h-4 w-4" />
                                            </a>
                                            <button onClick={() => handleDeleteDocument(doc.id)} className="text-red-600 hover:text-red-800">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default TenantManagement;
